import { hex, type Color } from "../../common/color.js";
import { Container, type ContainerOptions } from "../core/Container.js";
import type { RenderContext } from "../render/RenderContext.js";
import type { TextSpan } from "../render/RenderContext.js";
import type { FontSpec } from "../render/GlyphAtlas.js";
import type { Theme } from "../themes/Theme.js";
import { Control } from "../core/Control.js";

/** Which corner the stack grows from. */
export type ToastCorner = "top-left" | "top-right" | "bottom-left" | "bottom-right";

export interface ToastOptions {
  /** Plain text, or spans for a colored title over a body. */
  content: string | readonly TextSpan[];
  /** Seconds on screen. 0 keeps it up until it is dismissed by hand. */
  seconds?: number;
  /** A stripe down the leading edge; null for none. */
  accent?: "info" | "success" | "warning" | "error" | null;
  /** Called when the toast is clicked. Clicking always dismisses it. */
  onClick?: () => void;
}

export interface NotificationHostOptions extends ContainerOptions {
  corner?: ToastCorner;
  /** Widest a toast gets. */
  maxWidth?: number;
  /** Gap between stacked toasts, and from the edge. */
  gap?: number;
  /** Oldest toasts are dropped past this many. */
  maxVisible?: number;
  /** Default lifetime for toasts that do not name one. */
  defaultSeconds?: number;
  /**
   * Grow to cover the parent, so `corner` means a corner of the *screen*.
   * Turn it off to stack toasts inside a box of your own sizing.
   */
  fillParent?: boolean;
}

const PADDING_X = 12;
const PADDING_Y = 9;
const ACCENT_WIDTH = 3;
const FADE_SECONDS = 0.25;

/**
 * One toast. Made by `NotificationHost.show`; not built directly.
 */
export class Toast extends Control {
  readonly spans: readonly TextSpan[];
  readonly accent: ToastOptions["accent"];
  /** Frame time it was raised at, for the fade and the lifetime. */
  bornAt = 0;
  /** Seconds on screen; 0 means it stays until dismissed. */
  seconds: number;
  /** Wrapped lines, measured on first paint. */
  lines: TextSpan[][] = [];
  /** What the last layout was for: the host lays every toast out on every paint. */
  #laidOut: { maxWidth: number; font: FontSpec; size: number } | null = null;

  constructor(spans: readonly TextSpan[], options: ToastOptions) {
    super({ width: 260, height: 44, tabStop: false, cursor: "pointer" });
    this.spans = spans;
    this.accent = options.accent ?? null;
    this.seconds = options.seconds ?? 0;
    if (options.onClick) this.on("click", options.onClick);
  }

  /** Wrap to `maxWidth` and size to fit. Returns the height it settled on. */
  layoutFor(rc: RenderContext, theme: Theme, maxWidth: number): number {
    const font = theme.metrics.font;
    const last = this.#laidOut;
    if (last && last.maxWidth === maxWidth && last.font === font && last.size === font.size) {
      return this.height;
    }
    this.#laidOut = { maxWidth, font, size: font.size };
    const inner = maxWidth - PADDING_X * 2 - (this.accent ? ACCENT_WIDTH : 0);
    this.lines = rc.wrapSpans(this.spans, font, Math.max(20, inner));
    const widest = this.lines.reduce((w, l) => Math.max(w, rc.measureSpans(l, font)), 0);
    const step = rc.lineHeight(font);
    this.setSize(
      Math.min(maxWidth, Math.ceil(widest) + PADDING_X * 2 + (this.accent ? ACCENT_WIDTH : 0)),
      this.lines.length * step + PADDING_Y * 2,
    );
    return this.height;
  }

  protected override onPaint(rc: RenderContext, theme: Theme): void {
    const p = theme.palette;
    const r = this.localRect;
    const radius = theme.metrics.controlCornerRadius;

    rc.drawRect(r, {
      fill: this.hovered ? p.controlBgHover : p.controlBg,
      radius,
      borderWidth: 1,
      borderColor: p.controlBorder,
    });

    let left = PADDING_X;
    if (this.accent) {
      const stripe = accentColor(theme, this.accent);
      rc.fillRect({ x: 0, y: 1, width: ACCENT_WIDTH, height: r.height - 2 }, stripe, radius / 2);
      left += ACCENT_WIDTH;
    }

    const font = theme.metrics.font;
    rc.drawSpanBlock(
      this.lines,
      { x: left, y: PADDING_Y, width: r.width - left - PADDING_X, height: r.height - PADDING_Y * 2 },
      font,
      p.text,
      { lineHeight: rc.lineHeight(font) },
    );
  }
}

/**
 * A stack of transient messages in a corner of the desktop.
 *
 * Add one to the desktop's foreground layer and it will sit above every
 * window; each `show` puts a toast on the stack, which times out and takes
 * itself away. The same control serves "file saved" in an application and
 * "achievement unlocked" in a game.
 *
 * The host itself answers no hit test, so the gap between toasts is not a
 * dead patch of screen — only the toasts themselves take a click.
 */
export class NotificationHost extends Container {
  #toasts: Toast[] = [];
  #animating = false;

  corner: ToastCorner;
  maxWidth: number;
  gap: number;
  maxVisible: number;
  defaultSeconds: number;
  fillParent: boolean;

  /** The parent this is currently following for resizes. */
  #following: Container | null = null;

  constructor(options: NotificationHostOptions = {}) {
    super({ width: 320, height: 400, ...options });
    this.corner = options.corner ?? "bottom-right";
    this.maxWidth = options.maxWidth ?? 300;
    this.gap = options.gap ?? 8;
    this.maxVisible = options.maxVisible ?? 5;
    this.defaultSeconds = options.defaultSeconds ?? 4;
    this.fillParent = options.fillParent ?? true;
  }

  /**
   * Cover the parent, so the chosen corner is a corner of the screen rather
   * than of an arbitrary 320x400 box sitting at the origin.
   *
   * Done by following the parent's `resize` rather than by docking: a
   * `dock: "fill"` child takes part in the desktop's dock layout and would
   * eat the area a taskbar had claimed.
   */
  fitToParent(): void {
    if (!this.fillParent) return;
    const parent = this.parent;
    if (!parent) return;
    const area = parent.clientRect;
    if (this.x === area.x && this.y === area.y && this.width === area.width && this.height === area.height) {
      return;
    }
    this.setBounds(area.x, area.y, area.width, area.height);
  }

  #onParentResized = (): void => {
    this.fitToParent();
  };

  get toasts(): readonly Toast[] {
    return this.#toasts;
  }

  protected override hitTestSelf(): boolean {
    return false;
  }

  /** Raise a message. Returns the toast, so it can be dismissed early. */
  show(options: ToastOptions | string): Toast {
    const opts: ToastOptions = typeof options === "string" ? { content: options } : options;
    const spans = typeof opts.content === "string" ? [{ text: opts.content }] : opts.content;
    const toast = new Toast(spans, { ...opts, seconds: opts.seconds ?? this.defaultSeconds });
    toast.bornAt = this.desktop?.frameTime ?? 0;
    toast.on("click", () => this.dismiss(toast));

    this.#toasts.push(toast);
    this.controls.add(toast);
    // Oldest first out: a burst of twenty messages should leave the last few
    // readable rather than filling the screen.
    while (this.#toasts.length > this.maxVisible) this.dismiss(this.#toasts[0]!);

    this.#syncAnimation();
    this.invalidate();
    return toast;
  }

  dismiss(toast: Toast): void {
    const i = this.#toasts.indexOf(toast);
    if (i < 0) return;
    this.#toasts.splice(i, 1);
    this.controls.remove(toast);
    this.#syncAnimation();
    this.invalidate();
  }

  clear(): void {
    for (const toast of [...this.#toasts]) this.dismiss(toast);
  }

  /** Drop whatever has run out of time. */
  #expire(now: number): void {
    for (const toast of [...this.#toasts]) {
      if (toast.seconds > 0 && now - toast.bornAt >= toast.seconds) this.dismiss(toast);
    }
  }

  #syncAnimation(): void {
    const desktop = this.desktop;
    if (!desktop) {
      this.#animating = false;
      return;
    }
    // Only timed toasts need frames; a stack of sticky ones can sit still.
    const want = this.#toasts.some((t) => t.seconds > 0);
    if (want === this.#animating) return;
    this.#animating = want;
    if (want) desktop.addAnimator(this);
    else desktop.removeAnimator(this);
  }

  protected override onEnvironmentChanged(): void {
    const parent = this.parent;
    if (parent !== this.#following) {
      this.#following?.off("resize", this.#onParentResized);
      this.#following = parent;
      parent?.on("resize", this.#onParentResized);
    }
    this.fitToParent();
    this.#animating = false;
    this.#syncAnimation();
  }

  /** Stack the toasts into the chosen corner, newest nearest it. */
  #layout(rc: RenderContext, theme: Theme): void {
    const bottom = this.corner.startsWith("bottom");
    const right = this.corner.endsWith("right");
    let offset = this.gap;
    // Newest nearest the corner, so a new message never shifts the one being
    // read out from under the pointer.
    for (let i = this.#toasts.length - 1; i >= 0; i--) {
      const toast = this.#toasts[i]!;
      const height = toast.layoutFor(rc, theme, this.maxWidth);
      const x = right ? this.width - toast.width - this.gap : this.gap;
      const y = bottom ? this.height - offset - height : offset;
      toast.setLocation(Math.round(x), Math.round(y));
      offset += height + this.gap;
    }
  }

  protected override onPaint(rc: RenderContext, theme: Theme): void {
    const now = this.desktop?.frameTime ?? 0;
    this.#expire(now);
    this.#syncAnimation();
    this.#layout(rc, theme);
  }
}

/** The stripe color for each kind of message. */
function accentColor(theme: Theme, kind: NonNullable<ToastOptions["accent"]>): Color {
  const p = theme.palette;
  switch (kind) {
    // Fixed rather than drawn from the palette: "this went wrong" has to read
    // as wrong in every theme, and a theme whose accent is already red would
    // otherwise make an error indistinguishable from a notice.
    case "success":
      return hex("#43a85c");
    case "warning":
      return hex("#e5a829");
    case "error":
      return hex("#d44a4a");
    case "info":
    default:
      return p.accent;
  }
}

/** How long a toast has been fading for, 0..1. Exposed for a theme to use. */
export function toastFade(toast: Toast, now: number): number {
  if (toast.seconds <= 0) return 0;
  const left = toast.seconds - (now - toast.bornAt);
  return left >= FADE_SECONDS ? 0 : Math.max(0, 1 - left / FADE_SECONDS);
}
